import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Colors, Spacing, FontSizes, FontWeights, BorderRadius } from '../constants/theme';

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${Spacing.xxxl * 2}px ${Spacing.xl}px;
  text-align: center;
  min-height: 400px;
  background: ${Colors.surface};
  border: 1px solid ${Colors.border};
  border-radius: ${BorderRadius.lg}px;
`;

const EmptyStateIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, ${Colors.primary}20 0%, ${Colors.accent}10 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  margin-bottom: ${Spacing.xl}px;
  color: ${Colors.primary};
`;

const EmptyStateTitle = styled.h2`
  font-size: ${FontSizes.xl}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textPrimary};
  margin: 0 0 ${Spacing.md}px 0;
`;

const EmptyStateDescription = styled.p`
  font-size: ${FontSizes.md}px;
  color: ${Colors.textSecondary};
  margin: 0 0 ${Spacing.xl}px 0;
  max-width: 400px;
  line-height: 1.6;
`;

const EmptyStateActions = styled.div`
  display: flex;
  gap: ${Spacing.md}px;
  flex-wrap: wrap;
  justify-content: center;
`;

const PrimaryAction = styled.button`
  background: ${Colors.primary};
  color: ${Colors.textOnPrimary};
  border: none;
  padding: ${Spacing.md}px ${Spacing.xl}px;
  border-radius: ${BorderRadius.md}px;
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.medium};
  cursor: pointer;
  transition: background-color 200ms ease;
  
  &:hover {
    background: ${Colors.primaryDark};
  }
  
  &:focus {
    outline: 2px solid ${Colors.primary};
    outline-offset: 2px;
  }
`;

const SecondaryAction = styled.button`
  background: transparent;
  color: ${Colors.textSecondary};
  border: 1px solid ${Colors.border};
  padding: ${Spacing.md}px ${Spacing.xl}px;
  border-radius: ${BorderRadius.md}px;
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.medium};
  cursor: pointer;
  transition: all 200ms ease;
  
  &:hover {
    background: ${Colors.surfaceHover};
    border-color: ${Colors.borderDark};
    color: ${Colors.textPrimary};
  }
  
  &:focus {
    outline: 2px solid ${Colors.primary};
    outline-offset: 2px;
  }
`;

interface EmptyStateProps {
  icon?: string;
  title: string;
  description: string;
  primaryAction?: {
    label: string;
    onClick: () => void;
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
  ariaLabel?: string;
}

const EmptyState: React.FC<EmptyStateProps> = ({
  icon = "•",
  title,
  description,
  primaryAction,
  secondaryAction,
  ariaLabel
}) => {
  return (
    <EmptyStateContainer 
      role="status" 
      aria-label={ariaLabel || `${title}: ${description}`}
    >
      <EmptyStateIcon aria-hidden="true">{icon}</EmptyStateIcon>
      <EmptyStateTitle>{title}</EmptyStateTitle>
      <EmptyStateDescription>{description}</EmptyStateDescription>
      
      {(primaryAction || secondaryAction) && (
        <EmptyStateActions>
          {primaryAction && (
            <PrimaryAction 
              onClick={primaryAction.onClick}
              aria-label={`${primaryAction.label} - ${description}`}
            >
              {primaryAction.label}
            </PrimaryAction>
          )}
          {secondaryAction && (
            <SecondaryAction 
              onClick={secondaryAction.onClick}
              aria-label={`${secondaryAction.label} - ${description}`}
            >
              {secondaryAction.label}
            </SecondaryAction>
          )}
        </EmptyStateActions>
      )}
    </EmptyStateContainer>
  );
};

// Predefined Empty States for common scenarios
export const NoDataEmptyState: React.FC<{ onRefresh?: () => void }> = ({ onRefresh }) => {
  const { t } = useTranslation();
  return (
    <EmptyState
      icon="—"
      title={t('emptyState.noDataTitle', 'No Data Available')}
      description={t('emptyState.noDataDescription', "There's no data to display at the moment. This might be because filters are too restrictive or data hasn't been loaded yet.")}
      primaryAction={onRefresh ? {
        label: t('emptyState.refreshData', 'Refresh Data'),
        onClick: onRefresh
      } : undefined}
    />
  );
};

export const NoResultsEmptyState: React.FC<{ onClearFilters?: () => void }> = ({ onClearFilters }) => {
  const { t } = useTranslation();
  return (
    <EmptyState
      icon="•"
      title={t('emptyState.noResultsTitle', 'No Results Found')}
      description={t('emptyState.noResultsDescription', "We couldn't find any results matching your current search criteria. Try adjusting your filters or search terms.")}
      primaryAction={onClearFilters ? {
        label: t('emptyState.clearFilters', 'Clear Filters'),
        onClick: onClearFilters
      } : undefined}
    />
  );
};

export const ErrorEmptyState: React.FC<{ onRetry?: () => void }> = ({ onRetry }) => {
  const { t } = useTranslation();
  return (
    <EmptyState
      icon="!"
      title={t('emptyState.errorTitle', 'Something Went Wrong')}
      description={t('emptyState.errorDescription', 'We encountered an error while loading your data. Please try again or contact support if the problem persists.')}
      primaryAction={onRetry ? {
        label: t('emptyState.tryAgain', 'Try Again'),
        onClick: onRetry
      } : undefined}
    />
  );
};

export const WelcomeEmptyState: React.FC<{ onGetStarted?: () => void }> = ({ onGetStarted }) => {
  const { t } = useTranslation();
  return (
    <EmptyState
      icon="•"
      title={t('emptyState.welcomeTitle', 'Welcome to HR Analytics')}
      description={t('emptyState.welcomeDescription', 'Get started by connecting your data sources and setting up your first dashboard to gain insights into your organization.')}
      primaryAction={onGetStarted ? {
        label: t('emptyState.getStarted', 'Get Started'),
        onClick: onGetStarted
      } : undefined}
    />
  );
};

export default EmptyState;